import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Autoplay,
  Keyboard,
} from "swiper/modules";

// Estilos requeridos de Swiper
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Estilos del slider tipo galería
import "./SliderActividadesModerno.css";

/**
 * GaleriaCelda
 * Celda para cada cartel con soporte para:
 * - Slider centrado (PASO 1): el primer clic en una lateral la centra, en el centro abre enlace
 * - Apertura directa de enlace en pestaña nueva si ya está en el centro
 * - Botón flotante de enlace directo
 * - Modal Lightbox en pantalla completa si no dispone de URL
 */
const GaleriaCelda = ({
  item,
  index,
  isActive,
  usarSliderCentrado,
  hasExternalLink,
  handleImageClick,
  onOpenLightbox,
  usarEstiloNetflix,
}) => {
  const handleClick = (e) => {
    // Si está en modo slider centrado:
    if (usarSliderCentrado) {
      const isSlideActive =
        isActive ||
        Boolean(e.currentTarget.closest(".swiper-slide")?.classList.contains("swiper-slide-active"));

      // Si está en un lateral, no abre el enlace: el primer clic solo la centra (slideToClickedSlide)
      if (!isSlideActive) {
        return;
      }
    }

    // Si ya está en el centro (o si no está activo el modo centrado):
    if (item.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    } else {
      onOpenLightbox(index);
    }
    if (item.url2) window.open(item.url2, "_blank", "noopener,noreferrer");
    if (item.url3) window.open(item.url3, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="sam-galeria-celda celda"
      role="button"
      tabIndex={0}
      aria-label={`Actividad: ${item.alt || item.title || item.id}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      {/* Capa de fondo desenfocada si no es estilo Netflix */}
      {!usarEstiloNetflix && (
        <img
          src={`https://itetlax.org.mx/assets/img/imgCAR/${item.imgUrl}`}
          alt=""
          aria-hidden="true"
          className="sam-celda-bg-blur"
          loading="lazy"
          decoding="async"
        />
      )}

      {/* Imagen frontal nítida */}
      <img
        src={`https://itetlax.org.mx/assets/img/imgCAR/${item.imgUrl}`}
        alt={item.alt || item.title || "Actividad de Interés ITE"}
        className="sam-celda-img-frontal"
        loading={index < 4 ? "eager" : "lazy"}
        decoding="async"
      />

      {/* Botón flotante para enlace directo con píldora expandible 'Saber más' */}
      {hasExternalLink && (
        <button
          type="button"
          className="sam-celda-link-btn"
          aria-label={item.title || item.alt ? `Saber más sobre ${item.title || item.alt}` : "Saber más"}
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e);
          }}
        >
          <span className="saber-mas" aria-hidden="true">
            Saber más
          </span>
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * SliderActividadesModerno
 * Diseño tipo Galería: 3 imágenes simultáneas en celdas de idéntico tamaño.
 * Soporta modo Slider Centrado (todas pasan por el centro) y Lightbox.
 * Solo para Tablet y PC (ancho >= 768px).
 */
const SliderActividadesModerno = ({
  dataCAR = [],
  handleImageClick,
  slidesPorVistaTablet = 3,
}) => {
  // BANDERA PRINCIPAL: Slider Centrado (Tablet y PC)
  const USAR_SLIDER_CENTRADO = true;

  // Duración del temporizador de autoplay (ms) compartida entre Swiper y CSS
  const DURACION_AUTOPLAY = 5000;

  // Banderas de respaldo
  const USAR_ZOOM_SIMPLE = false;
  const USAR_CIRCULITOS = true;
  const USAR_ESTILO_BLANCO = true;
  const USAR_ESTILO_NETFLIX = false;
  const USAR_CINTILLO = false;

  // Estado para sincronizar pausa del temporizador cuando el mouse está encima
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);

  // Función para reiniciar la animación de la barrita activa con reflow forzado
  const reiniciarBarrita = useCallback(() => {
    requestAnimationFrame(() => {
      const container = document.querySelector(".circulitos-paginacion");
      if (!container) return;
      const activeBullet = container.querySelector(".swiper-pagination-bullet-active");
      if (activeBullet) {
        activeBullet.classList.add("sin-animacion");
        void activeBullet.offsetWidth; // Reflow forzado para reiniciar la animación
        activeBullet.classList.remove("sin-animacion");
      }
    });
  }, []);

  // Ordenar de forma descendente por ID igual que el slider original
  const dataOrdenada = useMemo(() => {
    if (!Array.isArray(dataCAR)) return [];
    return [...dataCAR].sort((a, b) => b.id - a.id);
  }, [dataCAR]);

  // Preparar lista de slides: si hay menos de 6 imágenes en total, se duplica la lista
  // para que el loop infinito siempre disponga de elementos suficientes a ambos lados
  const slidesData = useMemo(() => {
    if (!dataOrdenada.length) return [];
    if (dataOrdenada.length < 6) {
      return [
        ...dataOrdenada.map((item, idx) => ({ ...item, _loopKey: `orig-${item.id}-${idx}` })),
        ...dataOrdenada.map((item, idx) => ({ ...item, _loopKey: `dup-${item.id}-${idx}` })),
      ];
    }
    return dataOrdenada.map((item, idx) => ({ ...item, _loopKey: `item-${item.id}-${idx}` }));
  }, [dataOrdenada]);

  // Estado del visor a pantalla completa (Lightbox)
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isLightboxOpen = lightboxIndex !== null;
  const currentLightboxItem = isLightboxOpen ? dataOrdenada[lightboxIndex] : null;

  // Detección de preferencia de movimiento reducido
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReducedMotion(mediaQuery.matches);

      const handleChange = (e) => setReducedMotion(e.matches);
      mediaQuery.addEventListener?.("change", handleChange);
      return () => mediaQuery.removeEventListener?.("change", handleChange);
    }
  }, []);

  // Navegación en el Lightbox
  const handleLightboxPrev = useCallback((e) => {
    e?.stopPropagation();
    setLightboxIndex((prev) =>
      prev === 0 ? dataOrdenada.length - 1 : prev - 1
    );
  }, [dataOrdenada.length]);

  const handleLightboxNext = useCallback((e) => {
    e?.stopPropagation();
    setLightboxIndex((prev) =>
      prev === dataOrdenada.length - 1 ? 0 : prev + 1
    );
  }, [dataOrdenada.length]);

  const handleLightboxClose = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  // Control por teclado para el Lightbox (Escape, Flecha Izq, Flecha Der)
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleLightboxClose();
      } else if (e.key === "ArrowLeft") {
        handleLightboxPrev();
      } else if (e.key === "ArrowRight") {
        handleLightboxNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isLightboxOpen, handleLightboxClose, handleLightboxPrev, handleLightboxNext]);

  if (!dataOrdenada.length) {
    return null;
  }

  // Clases CSS dinámicas
  const sectionClasses = [
    "sam-section",
    "mb-5",
    USAR_ESTILO_BLANCO
      ? "estilo-blanco"
      : USAR_ESTILO_NETFLIX
      ? "estilo-netflix"
      : USAR_CINTILLO
      ? "cintillo-destacado"
      : "",
    USAR_CIRCULITOS ? "nav-circulitos" : "",
    USAR_SLIDER_CENTRADO
      ? "slider-centrado"
      : USAR_ZOOM_SIMPLE
      ? "zoom-simple"
      : "",
    isAutoplayPaused && USAR_SLIDER_CENTRADO ? "autoplay-pausado" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={sectionClasses}
      style={{ "--duracion-autoplay": `${DURACION_AUTOPLAY}ms` }}
    >
      <div className="sam-section-inner">
        {/* Título de la sección */}
        {USAR_ESTILO_BLANCO ? (
          <h3 className="bottom_title mt-3 mb-3 enlaces-custom">
            Actividades de Interés
          </h3>
        ) : USAR_ESTILO_NETFLIX ? (
          <div className="netflix-header">
            <div className="netflix-title-group">
              <h2 className="netflix-title">Actividades de Interés</h2>
              <div className="netflix-brand-bar" aria-hidden="true" />
            </div>
          </div>
        ) : (
          <>
            {!USAR_CINTILLO && (
              <h3 className="bottom_title mt-3 mb-3 enlaces-custom">
                Actividades de Interés
              </h3>
            )}
            {USAR_CINTILLO && (
              <h3 className="sam-banda-title">Actividades de Interés</h3>
            )}
          </>
        )}

        {/* Paginación por circulitos arriba y centrados para slider centrado */}
        {USAR_SLIDER_CENTRADO && (
          <div
            className="circulitos-paginacion"
            role="tablist"
            aria-label="Paginación de actividades"
          />
        )}

        <div className="sam-container">
          {/* Navegación flotante estilo Netflix */}
          {USAR_ESTILO_NETFLIX && (
            <div className="netflix-floating-nav">
              <button
                type="button"
                className="netflix-nav-btn netflix-nav-prev"
                aria-label="Anterior"
                title="Anterior"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                className="netflix-nav-btn netflix-nav-next"
                aria-label="Siguiente"
                title="Siguiente"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )}

          <Swiper
            modules={[Navigation, Pagination, Autoplay, Keyboard]}
            effect="slide"
            centeredSlides={USAR_SLIDER_CENTRADO ? true : false}
            slidesPerView={3}
            slidesPerGroup={1}
            slideToClickedSlide={USAR_SLIDER_CENTRADO ? true : false}
            loop={true}
            loopAdditionalSlides={3}
            watchSlidesProgress={true}
            slidesOffsetBefore={0}
            slidesOffsetAfter={0}
            spaceBetween={USAR_SLIDER_CENTRADO ? 20 : 16}
            speed={reducedMotion ? 0 : (USAR_SLIDER_CENTRADO ? 700 : 600)}
            grabCursor={true}
            keyboard={{ enabled: true }}
            autoplay={{
              delay: DURACION_AUTOPLAY,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            onSlideChange={() => {
              if (USAR_SLIDER_CENTRADO) reiniciarBarrita();
            }}
            onSliderMove={() => {
              if (USAR_SLIDER_CENTRADO) reiniciarBarrita();
            }}
            onTouchEnd={() => {
              if (USAR_SLIDER_CENTRADO) reiniciarBarrita();
            }}
            onPaginationUpdate={() => {
              if (USAR_SLIDER_CENTRADO) reiniciarBarrita();
            }}
            onAutoplayPause={() => {
              if (USAR_SLIDER_CENTRADO) setIsAutoplayPaused(true);
            }}
            onAutoplayResume={() => {
              if (USAR_SLIDER_CENTRADO) setIsAutoplayPaused(false);
            }}
            breakpoints={{
              768: {
                slidesPerView: 3,
                slidesPerGroup: 1,
                spaceBetween: USAR_SLIDER_CENTRADO ? 14 : 14,
                centeredSlides: USAR_SLIDER_CENTRADO ? true : false,
              },
              1024: {
                slidesPerView: 3,
                slidesPerGroup: 1,
                spaceBetween: USAR_SLIDER_CENTRADO ? 20 : 16,
                centeredSlides: USAR_SLIDER_CENTRADO ? true : false,
              },
            }}
            navigation={{
              prevEl: ".sam-btn-prev",
              nextEl: ".sam-btn-next",
            }}
            pagination={
              USAR_SLIDER_CENTRADO
                ? {
                    el: ".circulitos-paginacion",
                    clickable: true,
                    dynamicBullets: dataOrdenada.length > 12,
                    dynamicMainBullets: dataOrdenada.length > 12 ? 5 : undefined,
                    renderBullet: (index, className) => {
                      return `<button type="button" class="${className}" aria-label="Ir a la imagen ${index + 1}"></button>`;
                    },
                  }
                : USAR_ESTILO_NETFLIX
                ? false
                : {
                    el: ".sam-pagination",
                    clickable: true,
                    dynamicBullets: true,
                  }
            }
            className="sam-swiper"
          >
            {slidesData.map((item, index) => {
              const hasExternalLink = item.url || item.url2 || item.url3;

              return (
                <SwiperSlide key={item._loopKey || `sam-galeria-${item.id}-${index}`}>
                  {({ isActive }) => (
                    <GaleriaCelda
                      item={item}
                      index={index}
                      isActive={isActive}
                      usarSliderCentrado={USAR_SLIDER_CENTRADO}
                      hasExternalLink={hasExternalLink}
                      handleImageClick={handleImageClick}
                      onOpenLightbox={(idx) => setLightboxIndex(idx % dataOrdenada.length)}
                      usarEstiloNetflix={USAR_ESTILO_NETFLIX}
                    />
                  )}
                </SwiperSlide>
              );
            })}
          </Swiper>

          {/* Controles inferiores: completamente omitidos en slider centrado */}
          {!USAR_ESTILO_NETFLIX && !USAR_SLIDER_CENTRADO && (
            <div className="sam-controls-wrapper">
              <button
                type="button"
                className="sam-btn-nav sam-btn-prev"
                aria-label="Diapositivas anteriores"
                title="Anterior"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>

              <div className="sam-pagination" />

              <button
                type="button"
                className="sam-btn-nav sam-btn-next"
                aria-label="Diapositivas siguientes"
                title="Siguiente"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ====================================================================
          MODAL LIGHTBOX A PANTALLA COMPLETA
          ==================================================================== */}
      {isLightboxOpen && currentLightboxItem && (
        <div
          className="sam-lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Visor a pantalla completa"
          onClick={handleLightboxClose}
        >
          <div
            className="sam-lightbox-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón cerrar X */}
            <button
              type="button"
              className="sam-lightbox-close"
              aria-label="Cerrar visor"
              title="Cerrar (Esc)"
              onClick={handleLightboxClose}
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Flecha anterior */}
            <button
              type="button"
              className="sam-lightbox-arrow sam-lightbox-prev"
              aria-label="Imagen anterior"
              title="Anterior (Flecha izquierda)"
              onClick={handleLightboxPrev}
            >
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            {/* Contenedor e imagen grande del visor */}
            <div className="sam-lightbox-img-wrapper">
              <img
                src={`https://itetlax.org.mx/assets/img/imgCAR/${currentLightboxItem.imgUrl}`}
                alt={currentLightboxItem.alt || "Actividad ITE ampliada"}
                className="sam-lightbox-img"
              />
            </div>

            {/* Flecha siguiente */}
            <button
              type="button"
              className="sam-lightbox-arrow sam-lightbox-next"
              aria-label="Imagen siguiente"
              title="Siguiente (Flecha derecha)"
              onClick={handleLightboxNext}
            >
              <svg
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            {/* Pie del visor */}
            <div className="sam-lightbox-footer">
              <span className="sam-lightbox-counter">
                {lightboxIndex + 1} de {dataOrdenada.length}
              </span>

              {/* Botón de modal retirado según solicitud
              {currentLightboxItem.id === 6 && handleImageClick && (
                <button
                  type="button"
                  className="sam-lightbox-btn-action"
                  onClick={(e) => handleImageClick(e, currentLightboxItem)}
                >
                  Más información
                </button>
              )}
              */}

              {currentLightboxItem.url && (
                <a
                  href={currentLightboxItem.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sam-lightbox-btn-action"
                >
                  Abrir enlace
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              )}

              {currentLightboxItem.url2 && (
                <a
                  href={currentLightboxItem.url2}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sam-lightbox-btn-action"
                >
                  Enlace 2
                </a>
              )}

              {currentLightboxItem.url3 && (
                <a
                  href={currentLightboxItem.url3}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sam-lightbox-btn-action"
                >
                  Enlace 3
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SliderActividadesModerno;
